import React from "react";

import MNTNLogo from "@assets/mntn-logo.svg";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { ConnectionType } from "@north-beam/nb-client-side";
import { ConnectionCard } from "@pages/settings/connection-card";
import {
  type LoginComponentChildrenProps,
  type LoginComponentProps,
} from "@pages/settings/connection-status-manager";
import { PrimaryButton } from "@shared/buttons";

import { SupportedChannelsSelector } from "./shared/supported-channels-selector";

const type = ConnectionType.integration_mntn;

export function MNTNConnectionCard() {
  return (
    <ConnectionCard
      type={type}
      connectionTypeName="MNTN"
      connectionCallToAction={`
        Integrate your MNTN account
      `}
      logoUrl={MNTNLogo}
      logoAltText="MNTN Logo"
      successfulConnectionExtraText={() => null}
      knowledgeBaseLink="https://docs.northbeam.io/docs/tracking-for-mntn"
    >
      {({
        upsertConnection,
        isLoading,
        accounts,
      }: LoginComponentChildrenProps) => (
        <MNTNLoginComponent
          upsertConnection={upsertConnection}
          isLoading={isLoading}
          accounts={accounts}
        />
      )}
    </ConnectionCard>
  );
}

function MNTNLoginComponent({
  upsertConnection,
  isLoading,
}: Omit<LoginComponentProps, "setStatus" | "integrationDetails">) {
  const [apiKey, setApiKey] = React.useState("");
  const [selectedChannels, setSelectedChannels] = React.useState<string[]>([]);

  const onConnectClicked = React.useCallback(async () => {
    await upsertConnection({
      api_key: apiKey.trim(),
    });
  }, [apiKey, upsertConnection]);

  const availableChannels = [
    {
      value: "ctv",
      label: "Connected TV (CTV)",
      showWarningOnSelected: true,
    },
    {
      value: "multi-touch",
      label: "Multi-touch",
    },
  ];

  return (
    <div className="mb-3">
      <div className="flex flex-col gap-y-5">
        <div>
          <div>MNTN Channels</div>
          <SupportedChannelsSelector
            className="max-w-xl"
            selectedChannels={selectedChannels}
            initialOptions={availableChannels}
            onChange={setSelectedChannels}
            WarningMessageContainer={({ children, className }) => (
              <LearnMoreTooltip
                title="MNTN Channels"
                content="Attribution is unsupported for this channel"
                link="https://docs.northbeam.io/docs/tracking-for-mntn#important-attribution-notes"
                className={className}
              >
                {children}
              </LearnMoreTooltip>
            )}
            warningMessage="Attribution is unsupported for this channel."
          />
        </div>
        <div>
          <div>
            API Key
            <LearnMoreTooltip
              title="MNTN API Key"
              content="Provide your API Key"
              link="https://docs.northbeam.io/docs/tracking-for-mntn#step-3-generating-a-mntn-api-key"
            />
          </div>
          <input
            disabled={isLoading}
            type="text"
            className="max-w-xl form-control"
            value={apiKey}
            placeholder="API Key..."
            id="mntnApiKey"
            onChange={(event) => setApiKey(event.target.value)}
          />
        </div>
        <div>
          <PrimaryButton
            loading={isLoading}
            disabled={!apiKey.trim() || !selectedChannels.length}
            onClick={onConnectClicked}
          >
            {isLoading ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
